import React, { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import authService from "../services/authService";

const AuthContext = createContext(null);

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api/v1";

export function AuthProvider({ children }) {
  const [user, setUser] = useState({ token: null, role: null, email: null, userId: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function restoreSession() {
      const token = authService.getAccessToken();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;
        if (!decoded.exp || decoded.exp > now) {
          // Token still valid — restore session
          setUser({
            token,
            role:   decoded.role    ?? null,
            email:  decoded.email   ?? null,
            userId: decoded.user_id ?? null,
          });
          setLoading(false);
          return;
        }
        // Access token expired — attempt silent refresh
        const refreshToken = authService.getRefreshToken();
        if (!refreshToken) {
          authService.logout();
          setLoading(false);
          return;
        }
        const response = await axios.post(`${BASE_URL}/auth/refresh/`, { refresh: refreshToken });
        const newAccess = response.data.access;
        localStorage.setItem("access_token", newAccess);
        const newDecoded = jwtDecode(newAccess);
        setUser({
          token:  newAccess,
          role:   newDecoded.role    ?? null,
          email:  newDecoded.email   ?? null,
          userId: newDecoded.user_id ?? null,
        });
      } catch {
        // Refresh failed — clear tokens and force re-login
        authService.logout();
      } finally {
        setLoading(false);
      }
    }
    restoreSession();
  }, []);

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    const decoded = jwtDecode(data.access);
    setUser({
      token:  data.access,
      role:   decoded.role    ?? null,
      email:  decoded.email   ?? null,
      userId: decoded.user_id ?? null,
    });
    return data;
  };

  const logout = () => {
    authService.logout();
    setUser({ token: null, role: null, email: null, userId: null });
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: Boolean(user.token),
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
